import React, { useCallback, useEffect, useMemo, useState } from "react";
import AppLayout from "@/components/AppLayout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import {
  Plus, Users2, BookOpen, LineChart, Search, MoreVertical, Briefcase,
  ClipboardList, GraduationCap, Wallet,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import ProgramDrawer from "@/components/ProgramDrawer";
import {
  CATEGORIES, CLASSIFICATIONS, DELIVERY_MODES, DURATION_UNITS,
  PROGRAM_STATUSES, PAYMENT_PLANS, BATCH_STATUSES, ENROLMENT_STATUSES,
  PAYMENT_STATUSES, INTERNSHIP_STAGES, CAREER_SUPPORT_STAGES, CERTIFICATE_STATUSES,
  WORKFLOW_STATUSES,
  findLabel, findTone, formatDuration, formatMoney,
} from "@/lib/academyConstants";

const FINANCIAL_ROLES = ["super_admin", "founder", "ops_manager", "hr_admin"];
const MANAGE_ROLES = FINANCIAL_ROLES;

export default function Academy() {
  const { user } = useAuth();
  const canSeeFinancials = user && FINANCIAL_ROLES.includes(user.role);
  const canManage = user && MANAGE_ROLES.includes(user.role);
  const isSuperAdmin = user?.role === "super_admin";

  const [tab, setTab] = useState("programs");
  const [programs, setPrograms] = useState([]);
  const [enrolments, setEnrolments] = useState([]);
  const [batches, setBatches] = useState([]);
  const [stats, setStats] = useState(null);
  const [progOpen, setProgOpen] = useState(false);
  const [enrolOpen, setEnrolOpen] = useState(false);
  const [drawerProgram, setDrawerProgram] = useState(null);

  // Filters
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState({
    category: "all", classification: "all", delivery_mode: "all",
    status: "all", partner: "", batch_status: "all",
  });
  const [scope, setScope] = useState("active"); // active | archived

  const loadPrograms = useCallback(async () => {
    const params = {};
    if (q) params.q = q;
    if (filters.category !== "all") params.category = filters.category;
    if (filters.classification !== "all") params.classification = filters.classification;
    if (filters.delivery_mode !== "all") params.delivery_mode = filters.delivery_mode;
    if (filters.status !== "all") params.status = filters.status;
    if (filters.partner) params.partner = filters.partner;
    if (scope === "archived") {
      params.only_archived = true;
      params.include_demo = true;
    }
    const r = await api.get("/academy/programs", { params });
    setPrograms(r.data);
  }, [q, filters.category, filters.classification, filters.delivery_mode,
      filters.status, filters.partner, scope]);
  const loadEnrolments = async () => {
    const r = await api.get("/academy/enrolments");
    setEnrolments(r.data);
  };
  const loadBatches = useCallback(async () => {
    const params = filters.batch_status !== "all" ? { status: filters.batch_status } : {};
    const r = await api.get("/academy/batches", { params });
    setBatches(r.data);
  }, [filters.batch_status]);
  const loadStats = async () => {
    const r = await api.get("/academy/stats");
    setStats(r.data);
  };
  useEffect(() => { loadStats(); }, []);
  useEffect(() => { loadPrograms(); }, [loadPrograms]);
  useEffect(() => { if (tab === "enrolments") loadEnrolments(); }, [tab]);
  useEffect(() => { if (tab === "batches") loadBatches(); }, [tab, loadBatches]);

  const progById = useMemo(() => Object.fromEntries(programs.map(p => [p.id, p])), [programs]);

  const refreshAll = () => { loadPrograms(); loadEnrolments(); loadStats(); };

  return (
    <AppLayout title="Academy">
      {/* KPI cards — no placement rate */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <StatCard icon={BookOpen}    label="Active programs"       value={stats?.active_programs}      accent="#0EA5FF"/>
        <StatCard icon={Users2}      label="Total enrolments"      value={stats?.total_enrolments}     accent="#041B46"/>
        <StatCard icon={GraduationCap} label="Active learners"     value={stats?.active_learners}      accent="#5FBF3A"/>
        <StatCard icon={Briefcase}   label="Career support cases"  value={stats?.career_support_cases} accent="#F59E0B"/>
      </div>

      {/* Secondary strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
        <MiniStat label="Current batches"        value={stats?.current_batches}/>
        <MiniStat label="Completed learners"     value={stats?.completed_learners}/>
        <MiniStat label="Internship-active"      value={stats?.internship_active}/>
        {canSeeFinancials && (
          <MiniStat label="Outstanding fees" value={formatMoney(stats?.outstanding_fees)}/>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <TabsList data-testid="academy-tabs" className="bg-white border border-[var(--miro-border)]">
            <TabsTrigger value="programs" data-testid="tab-programs">Programs</TabsTrigger>
            <TabsTrigger value="batches" data-testid="tab-batches">Batches</TabsTrigger>
            <TabsTrigger value="enrolments" data-testid="tab-enrolments">Enrolments</TabsTrigger>
          </TabsList>
          <div className="flex items-center gap-2">
            {tab === "programs" && (
              <>
                <div className="flex bg-white border border-[var(--miro-border)] rounded-lg p-0.5">
                  {[["active", "Active"], ["archived", "Archived"]].map(([v, l]) => (
                    <button key={v} data-testid={`programs-scope-${v}`}
                            onClick={() => setScope(v)}
                            className={`h-8 px-3 rounded-md text-xs font-semibold transition-colors ${scope === v ? "bg-[var(--miro-navy)] text-white" : "text-[var(--miro-text-secondary)]"}`}>
                      {l}
                    </button>
                  ))}
                </div>
                {canManage && <NewProgramDialog open={progOpen} setOpen={setProgOpen} onCreated={refreshAll} canSeeFinancials={canSeeFinancials}/>}
              </>
            )}
            {tab === "enrolments" && (
              <NewEnrolmentDialog open={enrolOpen} setOpen={setEnrolOpen} programs={programs} onCreated={refreshAll}/>
            )}
          </div>
        </div>

        {/* PROGRAMS */}
        <TabsContent value="programs">
          <FiltersBar q={q} setQ={setQ} filters={filters} setFilters={setFilters}/>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mt-4">
            {programs.length === 0 && <Empty msg={scope === "archived" ? "No archived programs." : "No programs match your filters."}/>}
            {programs.map(p => (
              <ProgramCard key={p.id} p={p}
                           canManage={canManage} isSuperAdmin={isSuperAdmin}
                           onClick={() => setDrawerProgram(p)}
                           onChanged={refreshAll}/>
            ))}
          </div>
        </TabsContent>

        {/* BATCHES */}
        <TabsContent value="batches">
          <div className="flex items-center gap-2 mb-3">
            <Select value={filters.batch_status} onValueChange={v => setFilters({ ...filters, batch_status: v })}>
              <SelectTrigger data-testid="batches-filter-status" className="w-40 bg-white"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {BATCH_STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="bg-white border border-[var(--miro-border)] rounded-xl overflow-x-auto">
            <table data-testid="batches-table" className="w-full text-sm">
              <thead className="bg-[var(--miro-bg)] text-[var(--miro-text-secondary)]">
                <tr className="text-left">
                  <th className="px-4 py-3 font-semibold">Batch</th>
                  <th className="px-4 py-3 font-semibold">Program</th>
                  <th className="px-4 py-3 font-semibold">Start → End</th>
                  <th className="px-4 py-3 font-semibold">Mode</th>
                  <th className="px-4 py-3 font-semibold">Capacity</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {batches.length === 0 && (<tr><td colSpan={6} className="px-4 py-12 text-center text-[var(--miro-text-muted)]">No batches yet.</td></tr>)}
                {batches.map(b => {
                  const tone = findTone(BATCH_STATUSES, b.status);
                  return (
                    <tr key={b.id} data-testid={`batch-list-row-${b.id}`} className="border-t border-[var(--miro-border)]">
                      <td className="px-4 py-3 font-semibold text-[var(--miro-navy)]">{b.batch_code || b.id.slice(0, 8)}</td>
                      <td className="px-4 py-3 text-[var(--miro-text-secondary)]">{progById[b.program_id]?.title || "—"}</td>
                      <td className="px-4 py-3 text-xs text-[var(--miro-text-muted)]">
                        {b.start_date ? new Date(b.start_date).toLocaleDateString() : "—"} → {b.end_date ? new Date(b.end_date).toLocaleDateString() : "—"}
                      </td>
                      <td className="px-4 py-3 text-[var(--miro-text-secondary)]">{findLabel(DELIVERY_MODES, b.delivery_mode)}</td>
                      <td className="px-4 py-3 font-mono text-xs">{b.current_enrolments}/{b.capacity || 0}</td>
                      <td className="px-4 py-3">
                        <Badge className="border-0 font-semibold text-[10px]" style={{ background: tone?.bg, color: tone?.fg }}>
                          {findLabel(BATCH_STATUSES, b.status)}
                        </Badge>
                      </td>
                    </tr>);
                })}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* ENROLMENTS */}
        <TabsContent value="enrolments">
          <EnrolmentsTable enrolments={enrolments} progById={progById} canSeeFinancials={canSeeFinancials} onChanged={refreshAll}/>
        </TabsContent>
      </Tabs>

      <ProgramDrawer program={drawerProgram} open={!!drawerProgram} onClose={() => setDrawerProgram(null)}
                     onSaved={(u) => { setDrawerProgram(u); refreshAll(); }}
                     canManage={canManage} canSeeFinancials={canSeeFinancials}/>
    </AppLayout>
  );
}

/* ---------- Small building blocks ---------- */

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="miro-card p-5">
      <div className="flex items-start justify-between">
        <div className="miro-overline">{label}</div>
        <div className="w-9 h-9 rounded-lg flex items-center justify-center"
             style={{ background: `${accent}18`, color: accent }}>
          <Icon size={17}/>
        </div>
      </div>
      <div className="font-display font-black text-3xl mt-3 text-[var(--miro-navy)]">{value ?? "—"}</div>
    </div>
  );
}
function MiniStat({ label, value, muted }) {
  return (
    <div className="bg-white border border-[var(--miro-border)] rounded-lg p-3">
      <div className="miro-overline">{label}</div>
      <div className={`font-display font-bold text-xl mt-1 ${muted ? "text-[var(--miro-text-muted)]" : "text-[var(--miro-navy)]"}`}>
        {value ?? "—"}
      </div>
    </div>
  );
}
function Empty({ msg }) {
  return <div className="col-span-full text-center py-16 text-[var(--miro-text-muted)]">{msg}</div>;
}

function FiltersBar({ q, setQ, filters, setFilters }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="flex items-center gap-2 bg-white border border-[var(--miro-border)] rounded-lg px-3 py-2 flex-1 min-w-[220px] max-w-md">
        <Search size={15} className="text-[var(--miro-text-muted)]"/>
        <input data-testid="programs-search" className="bg-transparent outline-none text-sm flex-1"
               placeholder="Search by name or program ID…" value={q} onChange={e => setQ(e.target.value)}/>
      </div>
      <FilterSelect value={filters.category} onChange={v => setFilters({ ...filters, category: v })}
                    testid="filter-category" placeholder="Category" options={CATEGORIES}/>
      <FilterSelect value={filters.classification} onChange={v => setFilters({ ...filters, classification: v })}
                    testid="filter-classification" placeholder="Classification" options={CLASSIFICATIONS}/>
      <FilterSelect value={filters.delivery_mode} onChange={v => setFilters({ ...filters, delivery_mode: v })}
                    testid="filter-delivery-mode" placeholder="Delivery" options={DELIVERY_MODES}/>
      <FilterSelect value={filters.status} onChange={v => setFilters({ ...filters, status: v })}
                    testid="filter-status" placeholder="Status" options={PROGRAM_STATUSES}/>
      <Input data-testid="filter-partner" placeholder="Partner…" className="w-40 bg-white"
             value={filters.partner} onChange={e => setFilters({ ...filters, partner: e.target.value })}/>
    </div>
  );
}
function FilterSelect({ value, onChange, testid, placeholder, options }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="w-40 bg-white"><SelectValue placeholder={placeholder}/></SelectTrigger>
      <SelectContent>
        <SelectItem value="all">All {placeholder.toLowerCase()}s</SelectItem>
        {options.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

/* ---------- Program card ---------- */
function ProgramCard({ p, canManage, isSuperAdmin, onClick, onChanged }) {
  const cls = findTone(CLASSIFICATIONS, p.classification);
  const st  = findTone(PROGRAM_STATUSES, p.status);
  const [confirmDel, setConfirmDel] = useState(false);

  const doAction = async (action) => {
    try {
      if (action === "mark_inactive") {
        await api.post(`/academy/programs/${p.id}/mark-inactive`);
        toast.success("Program marked inactive");
      } else if (action === "archive") {
        const reason = window.prompt("Reason for archiving (optional):", "");
        await api.post(`/academy/programs/${p.id}/archive`, { reason: reason || "" });
        toast.success("Archived");
      } else if (action === "restore") {
        await api.post(`/academy/programs/${p.id}/restore`);
        toast.success("Restored");
      } else if (action === "duplicate") {
        await api.post(`/academy/programs/${p.id}/duplicate`);
        toast.success("Duplicated as Draft");
      }
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid={`program-card-${p.id}`} className="miro-card p-5 flex flex-col cursor-pointer hover:shadow-md transition-shadow"
         onClick={onClick}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="miro-overline">{p.program_code || "PROGRAM"}</div>
          <h3 className="font-display font-bold text-lg text-[var(--miro-navy)] leading-tight mt-1 line-clamp-2">{p.title}</h3>
        </div>
        {canManage && (
          <div onClick={e => e.stopPropagation()}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid={`program-menu-${p.id}`} className="text-[var(--miro-text-muted)] hover:text-[var(--miro-navy)] p-1 rounded">
                  <MoreVertical size={16}/>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-white">
                <DropdownMenuItem onClick={onClick}>View</DropdownMenuItem>
                <DropdownMenuItem onClick={onClick}>Edit</DropdownMenuItem>
                <DropdownMenuItem data-testid={`program-duplicate-${p.id}`} onClick={() => doAction("duplicate")}>Duplicate</DropdownMenuItem>
                {p.status !== "inactive" && !p.archived && (
                  <DropdownMenuItem data-testid={`program-inactive-${p.id}`} onClick={() => doAction("mark_inactive")}>
                    Mark inactive
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator/>
                {p.archived ? (
                  <DropdownMenuItem data-testid={`program-restore-${p.id}`} onClick={() => doAction("restore")}>
                    Restore
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem data-testid={`program-archive-${p.id}`} onClick={() => doAction("archive")}>
                    Archive
                  </DropdownMenuItem>
                )}
                {isSuperAdmin && (
                  <>
                    <DropdownMenuSeparator/>
                    <DropdownMenuItem data-testid={`program-perma-delete-${p.id}`} onClick={() => setConfirmDel(true)}
                                      className="text-red-600 focus:text-red-700">
                      Permanent delete…
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        <Badge className="border-0 font-semibold text-[10px]" style={{ background: cls?.bg, color: cls?.fg }}>
          {findLabel(CLASSIFICATIONS, p.classification)}
        </Badge>
        <Badge className="border-0 font-semibold text-[10px]" style={{ background: "#E0F2FE", color: "#0284C7" }}>
          {findLabel(CATEGORIES, p.category)}
        </Badge>
      </div>
      <p className="text-sm text-[var(--miro-text-secondary)] mt-2 line-clamp-2">{p.description || "—"}</p>

      <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-[var(--miro-text-secondary)]">
        <div>Duration: <span className="text-[var(--miro-navy)] font-medium">{formatDuration(p)}</span></div>
        <div>Mode: <span className="text-[var(--miro-navy)] font-medium capitalize">{p.delivery_mode}</span></div>
        <div>Fee: <span className="text-[var(--miro-navy)] font-mono font-semibold">{formatMoney(p.fee)}</span></div>
        <div>Enrolments: <span className="text-[var(--miro-navy)] font-mono font-semibold">{p.current_enrolments}/{p.capacity || 0}</span></div>
      </div>

      <div className="mt-4 pt-3 border-t border-[var(--miro-border)] flex items-center justify-between">
        <div className="flex flex-wrap items-center gap-1">
          <Badge className="border-0 font-semibold text-[10px]" style={{ background: st?.bg, color: st?.fg }}>
            {p.archived ? "Archived" : findLabel(PROGRAM_STATUSES, p.status)}
          </Badge>
          {p.workflow_status && p.workflow_status !== "active" && (
            <Badge className="border-0 font-semibold text-[10px]"
                   style={{ background: findTone(WORKFLOW_STATUSES, p.workflow_status)?.bg,
                            color: findTone(WORKFLOW_STATUSES, p.workflow_status)?.fg }}>
              {findLabel(WORKFLOW_STATUSES, p.workflow_status)}
            </Badge>
          )}
        </div>
        {p.batch_start_date && (
          <div className="text-[11px] text-[var(--miro-text-muted)]">
            Next: {new Date(p.batch_start_date).toLocaleDateString()}
          </div>
        )}
      </div>

      <PermanentDeleteDialog open={confirmDel} onClose={() => setConfirmDel(false)}
                             program={p} onDeleted={onChanged}/>
    </div>
  );
}

function PermanentDeleteDialog({ open, onClose, program, onDeleted }) {
  const [reason, setReason] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const canSubmit = reason.trim().length >= 8 && confirm === "DELETE";
  const submit = async () => {
    setSaving(true);
    try {
      await api.delete(`/academy/programs/${program.id}`, { data: { reason, confirm: true } });
      toast.success("Program permanently deleted");
      onClose(); onDeleted?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white" data-testid="program-perma-delete-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl text-red-600">Permanent delete</DialogTitle></DialogHeader>
        <div className="text-sm text-[var(--miro-text-secondary)]">
          Program <span className="font-semibold text-[var(--miro-navy)]">{program.title}</span> will be permanently removed.
          This cannot be undone. Consider archiving instead.
        </div>
        <div className="space-y-2 mt-2">
          <div>
            <Label>Reason (min 8 chars)</Label>
            <Textarea rows={2} data-testid="program-perma-reason" value={reason} onChange={e => setReason(e.target.value)} className="mt-1"/>
          </div>
          <div>
            <Label>Type <span className="font-mono">DELETE</span> to confirm</Label>
            <Input data-testid="program-perma-confirm" value={confirm} onChange={e => setConfirm(e.target.value)} className="mt-1"/>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="program-perma-submit" disabled={!canSubmit || saving} onClick={submit}
                  style={{ background: "#B91C1C", color: "white" }}>
            {saving ? "Deleting…" : "Permanently delete"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- New program dialog ---------- */
function NewProgramDialog({ open, setOpen, onCreated, canSeeFinancials }) {
  const empty = {
    title: "", tagline: "", program_code: "", description: "",
    category: "it", classification: "miro_in_house", delivery_mode: "hybrid",
    delivery_partner: "MIRO", duration_value: 12, duration_unit: "weeks",
    fee: 0, starts_from: false, gst_applicable: false,
    admission_fee_applicable: false, admission_fee: 2000, emi_available: false,
    max_discount_pct: 10, partner_cost: 0, capacity: 30,
    status: "active", workflow_status: "draft",
    featured: false, display_order: 100,
    payment_plan: "one_time", curriculum_url: "",
  };
  const [f, setF] = useState(empty);
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setF(x => ({ ...x, [k]: v }));

  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/academy/programs", {
        ...f,
        duration_value: Number(f.duration_value) || 0,
        fee: Number(f.fee) || 0,
        max_discount_pct: Number(f.max_discount_pct) || 0,
        partner_cost: Number(f.partner_cost) || 0,
        capacity: Number(f.capacity) || 0,
        admission_fee: Number(f.admission_fee) || 0,
        display_order: Number(f.display_order) || 100,
      });
      toast.success("Program created");
      setOpen(false); setF(empty); onCreated?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="new-program-btn" style={{ background: "var(--miro-navy)" }}>
          <Plus size={16} className="mr-1"/> New program
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-white sm:max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display text-2xl text-[var(--miro-navy)]">New program</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Title *</Label><Input data-testid="new-program-title" value={f.title} onChange={e => upd("title", e.target.value)} className="mt-1"/></div>
          <div className="col-span-2"><Label>Tagline</Label><Input data-testid="new-program-tagline" value={f.tagline} onChange={e => upd("tagline", e.target.value)} className="mt-1" placeholder="One-line pitch shown on public cards"/></div>
          <div><Label>Program code</Label><Input data-testid="new-program-code" value={f.program_code} onChange={e => upd("program_code", e.target.value)} className="mt-1"/></div>
          <div>
            <Label>Category *</Label>
            <Select value={f.category} onValueChange={v => upd("category", v)}>
              <SelectTrigger data-testid="new-program-category" className="mt-1"><SelectValue/></SelectTrigger>
              <SelectContent>{CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Classification *</Label>
            <Select value={f.classification} onValueChange={v => upd("classification", v)}>
              <SelectTrigger data-testid="new-program-classification" className="mt-1"><SelectValue/></SelectTrigger>
              <SelectContent>{CLASSIFICATIONS.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Delivery mode *</Label>
            <Select value={f.delivery_mode} onValueChange={v => upd("delivery_mode", v)}>
              <SelectTrigger data-testid="new-program-mode" className="mt-1"><SelectValue/></SelectTrigger>
              <SelectContent>{DELIVERY_MODES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Delivery partner</Label><Input value={f.delivery_partner} onChange={e => upd("delivery_partner", e.target.value)} className="mt-1"/></div>
          <div><Label>Duration *</Label><Input data-testid="new-program-duration" type="number" min={0} value={f.duration_value} onChange={e => upd("duration_value", e.target.value)} className="mt-1"/></div>
          <div>
            <Label>Unit</Label>
            <Select value={f.duration_unit} onValueChange={v => upd("duration_unit", v)}>
              <SelectTrigger className="mt-1"><SelectValue/></SelectTrigger>
              <SelectContent>{DURATION_UNITS.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          {/* Pricing */}
          <div className="col-span-2 miro-overline mt-2">Pricing</div>
          <div><Label>Fee (₹) *</Label><Input data-testid="new-program-fee" type="number" min={0} value={f.fee} onChange={e => upd("fee", e.target.value)} className="mt-1"/></div>
          <div className="flex items-center gap-3 pt-6">
            <label className="flex items-center gap-2 text-sm text-[var(--miro-text-secondary)]">
              <input type="checkbox" data-testid="new-program-startsfrom" checked={f.starts_from} onChange={e => upd("starts_from", e.target.checked)}/>
              Show "Starts from"
            </label>
            <label className="flex items-center gap-2 text-sm text-[var(--miro-text-secondary)]">
              <input type="checkbox" data-testid="new-program-gst" checked={f.gst_applicable} onChange={e => upd("gst_applicable", e.target.checked)}/>
              + GST
            </label>
          </div>
          <div><Label>Max discount (%)</Label><Input type="number" min={0} max={100} value={f.max_discount_pct} onChange={e => upd("max_discount_pct", e.target.value)} className="mt-1"/></div>
          {canSeeFinancials && (
            <div><Label>Partner cost / seat (₹)</Label><Input data-testid="new-program-partner-cost" type="number" min={0} value={f.partner_cost} onChange={e => upd("partner_cost", e.target.value)} className="mt-1"/></div>
          )}
          <div className="flex items-center gap-2 pt-6">
            <input type="checkbox" data-testid="new-program-emi" checked={f.emi_available} onChange={e => upd("emi_available", e.target.checked)}/>
            <Label className="mb-0">EMI options available</Label>
          </div>
          <div className="flex items-center gap-2 pt-6">
            <input type="checkbox" data-testid="new-program-adm-fee" checked={f.admission_fee_applicable} onChange={e => upd("admission_fee_applicable", e.target.checked)}/>
            <Label className="mb-0">Admission fee applies</Label>
          </div>
          <div><Label>Admission fee (₹)</Label><Input type="number" min={0} value={f.admission_fee} onChange={e => upd("admission_fee", e.target.value)} className="mt-1" disabled={!f.admission_fee_applicable}/></div>

          {/* Publication */}
          <div className="col-span-2 miro-overline mt-2">Publication</div>
          <div><Label>Capacity</Label><Input type="number" min={0} value={f.capacity} onChange={e => upd("capacity", e.target.value)} className="mt-1"/></div>
          <div><Label>Display order</Label><Input type="number" min={0} value={f.display_order} onChange={e => upd("display_order", e.target.value)} className="mt-1"/></div>
          <div>
            <Label>Status *</Label>
            <Select value={f.status} onValueChange={v => upd("status", v)}>
              <SelectTrigger data-testid="new-program-status" className="mt-1"><SelectValue/></SelectTrigger>
              <SelectContent>{PROGRAM_STATUSES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Workflow *</Label>
            <Select value={f.workflow_status} onValueChange={v => upd("workflow_status", v)}>
              <SelectTrigger data-testid="new-program-workflow" className="mt-1"><SelectValue/></SelectTrigger>
              <SelectContent>{WORKFLOW_STATUSES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
            </Select>
            <div className="text-[10px] text-[var(--miro-text-muted)] mt-1">Only Approved + Active programs appear on the public catalogue.</div>
          </div>
          <div className="flex items-center gap-2 pt-6">
            <input type="checkbox" data-testid="new-program-featured" checked={f.featured} onChange={e => upd("featured", e.target.checked)}/>
            <Label className="mb-0">Featured on catalogue</Label>
          </div>
          <div>
            <Label>Payment plan</Label>
            <Select value={f.payment_plan} onValueChange={v => upd("payment_plan", v)}>
              <SelectTrigger className="mt-1"><SelectValue/></SelectTrigger>
              <SelectContent>{PAYMENT_PLANS.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="col-span-2"><Label>Curriculum URL</Label><Input value={f.curriculum_url} onChange={e => upd("curriculum_url", e.target.value)} className="mt-1" placeholder="https://…"/></div>
          <div className="col-span-2"><Label>Description</Label><Textarea rows={3} value={f.description} onChange={e => upd("description", e.target.value)} className="mt-1"/></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button data-testid="new-program-submit"
                  disabled={!f.title.trim() || !f.category || !f.classification || !f.delivery_mode || !f.status || saving}
                  onClick={submit} style={{ background: "var(--miro-navy)" }}>
            {saving ? "Creating…" : "Create program"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Enrolments ---------- */
function EnrolmentsTable({ enrolments, progById, canSeeFinancials, onChanged }) {
  const [ef, setEf] = useState({ program_id: "all", batch_id: "all", counsellor_id: "all", payment_status: "all", status: "all", date_from: "", date_to: "" });
  const filtered = useMemo(() => {
    return enrolments.filter(e => {
      if (ef.program_id !== "all" && e.program_id !== ef.program_id) return false;
      if (ef.status !== "all" && e.status !== ef.status) return false;
      if (ef.payment_status !== "all" && e.payment_status !== ef.payment_status) return false;
      if (ef.date_from && e.created_at < ef.date_from) return false;
      if (ef.date_to && e.created_at > ef.date_to + "T23:59:59Z") return false;
      return true;
    });
  }, [enrolments, ef]);

  const recordPayment = async (id, amount) => {
    if (!amount) return;
    try { await api.post(`/academy/enrolments/${id}/payment`, { amount: Number(amount) }); toast.success("Payment recorded"); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Payment failed"); }
  };

  return (
    <>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <FilterSelect value={ef.program_id} onChange={v => setEf({ ...ef, program_id: v })}
                      testid="enrol-filter-program" placeholder="Program" options={Object.values(progById).map(p => ({ value: p.id, label: p.title }))}/>
        <FilterSelect value={ef.status} onChange={v => setEf({ ...ef, status: v })}
                      testid="enrol-filter-status" placeholder="Status" options={ENROLMENT_STATUSES}/>
        <FilterSelect value={ef.payment_status} onChange={v => setEf({ ...ef, payment_status: v })}
                      testid="enrol-filter-payment" placeholder="Payment" options={PAYMENT_STATUSES}/>
        <Input type="date" data-testid="enrol-filter-date-from" className="w-40 bg-white" value={ef.date_from} onChange={e => setEf({ ...ef, date_from: e.target.value })}/>
        <Input type="date" data-testid="enrol-filter-date-to" className="w-40 bg-white" value={ef.date_to} onChange={e => setEf({ ...ef, date_to: e.target.value })}/>
      </div>
      <div className="bg-white border border-[var(--miro-border)] rounded-xl overflow-x-auto">
        <table data-testid="enrolments-table" className="w-full text-sm">
          <thead className="bg-[var(--miro-bg)] text-[var(--miro-text-secondary)]">
            <tr className="text-left">
              <th className="px-4 py-3 font-semibold">Student</th>
              <th className="px-4 py-3 font-semibold">Program</th>
              <th className="px-4 py-3 font-semibold">Fee / Discount</th>
              <th className="px-4 py-3 font-semibold">Paid / Balance</th>
              <th className="px-4 py-3 font-semibold">Payment</th>
              <th className="px-4 py-3 font-semibold">Status</th>
              <th className="px-4 py-3 font-semibold">Career</th>
              <th className="px-4 py-3 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (<tr><td colSpan={8} className="px-4 py-12 text-center text-[var(--miro-text-muted)]">No enrolments match.</td></tr>)}
            {filtered.map(e => {
              const pay = findTone(PAYMENT_STATUSES, e.payment_status);
              const st  = findTone(ENROLMENT_STATUSES, e.status);
              return (
                <tr key={e.id} data-testid={`enrol-row-${e.id}`} className="border-t border-[var(--miro-border)]">
                  <td className="px-4 py-3">
                    <div className="font-semibold text-[var(--miro-navy)]">{e.student_name}</div>
                    <div className="text-xs text-[var(--miro-text-muted)]">{e.student_id || "—"}</div>
                  </td>
                  <td className="px-4 py-3 text-[var(--miro-text-secondary)] text-xs">{progById[e.program_id]?.title || "—"}</td>
                  <td className="px-4 py-3 text-xs font-mono">{formatMoney(e.fee)} / {formatMoney(e.discount)}</td>
                  <td className="px-4 py-3 text-xs font-mono">
                    <div>{formatMoney(e.amount_paid)}</div>
                    <div className={e.balance > 0 ? "text-red-600" : "text-[var(--miro-text-muted)]"}>Bal {formatMoney(e.balance)}</div>
                  </td>
                  <td className="px-4 py-3">
                    <Badge className="border-0 font-semibold text-[10px]" style={{ background: pay?.bg, color: pay?.fg }}>
                      {findLabel(PAYMENT_STATUSES, e.payment_status)}
                    </Badge>
                  </td>
                  <td className="px-4 py-3">
                    <Badge className="border-0 font-semibold text-[10px]" style={{ background: st?.bg, color: st?.fg }}>
                      {findLabel(ENROLMENT_STATUSES, e.status)}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-xs text-[var(--miro-text-secondary)]">
                    {findLabel(CAREER_SUPPORT_STAGES, e.career_support_stage)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button size="sm" variant="outline" data-testid={`enrol-record-pay-${e.id}`}
                            onClick={() => {
                              const amt = window.prompt("Amount to record (₹):", "");
                              if (amt) recordPayment(e.id, amt);
                            }}>
                      Record payment
                    </Button>
                  </td>
                </tr>);
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

function NewEnrolmentDialog({ open, setOpen, programs, onCreated }) {
  const empty = {
    student_name: "", student_id: "", program_id: "", batch_id: "",
    fee: 0, discount: 0, amount_paid: 0,
    payment_status: "not_started", status: "enrolled",
    internship_stage: "not_applicable", career_support_stage: "not_started",
    certificate_status: "not_issued", next_action: "",
  };
  const [f, setF] = useState(empty);
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setF(x => ({ ...x, [k]: v }));
  const chosen = programs.find(p => p.id === f.program_id);
  useEffect(() => {
    if (chosen && f.fee === 0) setF(x => ({ ...x, fee: chosen.fee || 0 }));
    // eslint-disable-next-line
  }, [f.program_id]);

  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/academy/enrolments", {
        ...f,
        fee: Number(f.fee) || 0, discount: Number(f.discount) || 0,
        amount_paid: Number(f.amount_paid) || 0,
      });
      toast.success("Enrolment created");
      setOpen(false); setF(empty); onCreated?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="new-enrolment-btn" style={{ background: "var(--miro-navy)" }}>
          <Plus size={16} className="mr-1"/> New enrolment
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-white max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display text-2xl text-[var(--miro-navy)]">New enrolment</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Student name *</Label><Input data-testid="new-enrolment-name" value={f.student_name} onChange={e => upd("student_name", e.target.value)} className="mt-1"/></div>
          <div><Label>Student ID</Label><Input data-testid="new-enrolment-student-id" value={f.student_id} onChange={e => upd("student_id", e.target.value)} className="mt-1"/></div>
          <div className="col-span-2">
            <Label>Program *</Label>
            <Select value={f.program_id} onValueChange={v => upd("program_id", v)}>
              <SelectTrigger data-testid="new-enrolment-program" className="mt-1"><SelectValue placeholder="Pick a program"/></SelectTrigger>
              <SelectContent>{programs.map(p => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Fee (₹)</Label><Input type="number" min={0} value={f.fee} onChange={e => upd("fee", e.target.value)} className="mt-1"/></div>
          <div><Label>Discount (₹)</Label><Input data-testid="new-enrolment-discount" type="number" min={0} value={f.discount} onChange={e => upd("discount", e.target.value)} className="mt-1"/>
            {chosen && <div className="text-[10px] text-[var(--miro-text-muted)] mt-0.5">Max approved discount: {chosen.max_discount_pct}% = {formatMoney((chosen.fee || 0) * (chosen.max_discount_pct || 0) / 100)}</div>}
          </div>
          <div><Label>Amount paid (₹)</Label><Input type="number" min={0} value={f.amount_paid} onChange={e => upd("amount_paid", e.target.value)} className="mt-1"/></div>
          <div>
            <Label>Payment status</Label>
            <Select value={f.payment_status} onValueChange={v => upd("payment_status", v)}>
              <SelectTrigger className="mt-1"><SelectValue/></SelectTrigger>
              <SelectContent>{PAYMENT_STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Enrolment status</Label>
            <Select value={f.status} onValueChange={v => upd("status", v)}>
              <SelectTrigger className="mt-1"><SelectValue/></SelectTrigger>
              <SelectContent>{ENROLMENT_STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Internship stage</Label>
            <Select value={f.internship_stage} onValueChange={v => upd("internship_stage", v)}>
              <SelectTrigger className="mt-1"><SelectValue/></SelectTrigger>
              <SelectContent>{INTERNSHIP_STAGES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Career support stage</Label>
            <Select value={f.career_support_stage} onValueChange={v => upd("career_support_stage", v)}>
              <SelectTrigger className="mt-1"><SelectValue/></SelectTrigger>
              <SelectContent>{CAREER_SUPPORT_STAGES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="col-span-2"><Label>Next action</Label><Input value={f.next_action} onChange={e => upd("next_action", e.target.value)} className="mt-1"/></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button data-testid="new-enrolment-submit"
                  disabled={!f.student_name.trim() || !f.program_id || saving}
                  onClick={submit} style={{ background: "var(--miro-navy)" }}>
            {saving ? "Creating…" : "Enrol student"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
